import { LitElement, html, nothing } from "lit";
import { fa, onceParentAvailable, createWebComp } from "../../../utils/widgets.js";
import {
	faChevronDown, faLayerGroup, faXmark
} from "@fortawesome/free-solid-svg-icons";
import { activeIcon } from "../../styles.js";

/**
 * Collapsible button showing off map filters menus.
 * @class Panoramax.components.ui.widgets.MapLayersButton
 * @element pnx-widget-maplayers
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-widget-maplayers _parent=${viewer} />
 * ```
 */
export default class MapLayersButton extends LitElement {
	/** @private */
	static styles = [activeIcon];

	/** @private */
	static properties = {
		_active: {state: true},
	};

	constructor() {
		super();
		this._active = false;
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();

		const nullThemes = [undefined, null, "", "default"];

		onceParentAvailable(this).then(() => this._parent?.onceMapReady?.().then(() => {
			this._active = !nullThemes.includes(this._parent.mapStyleComposer.panoramaxTheme);
			this._parent.mapStyleComposer.addEventListener("theme-changed", e => {
				this._active = !nullThemes.includes(e.detail.theme);
			});
		}));
	}

	/** @private */
	_toggleDrawer() {
		if(this._parent?.isBottomDrawerVisible?.()) {
			const bottomDrawerContent = this._parent.bottomDrawer.shadowRoot.querySelector("slot")?.assignedNodes();
			if(bottomDrawerContent.find(c => c.tagName.toLowerCase() === "pnx-map-layers-menu")) {
				this._parent?.setBottomDrawer(false);
				return;
			}
		}

		this._parent?.setBottomDrawer?.(true, [createWebComp(
			"pnx-map-layers-menu",
			{ id: "pnx-map-layers-menu", _parent: this._parent }
		)], {autoadjust: true, autoclose: true});
	}

	/** @private */
	render() {
		const isSmallSized = this._parent?.isWidthSmall();
		const isMedSized = this._parent?.isWidthMedium();
		const resetIcon = fa(faXmark, {styles: {"pointer-events": "auto"}});
		resetIcon.addEventListener("click", e => {
			e.stopPropagation();
			this._parent.mapStyleComposer.setPanoramaxTheme("default");
		});

		return isSmallSized ? html`
			<pnx-button kind="flat" size="xl" slot="button" @click=${this._toggleDrawer}>
				${fa(faLayerGroup)}
				${this._active ? html`<span class="pnx-active-icon"></span>` : nothing}
			</pnx-button>
		` : html`
			<pnx-togglable-group
				id="pnx-widget-map-layers"
				class="pnx-print-hidden"
				nobg
				padded="false"
				over-button
				._parent=${this._parent}
			>
				<pnx-button kind="flat" size="xl" slot="button">
					${fa(faLayerGroup)}
					${this._active ? html`<span class="pnx-active-icon"></span>` : nothing}
					${isMedSized ? nothing : this._parent?._t.pnx.layers}
					${isMedSized ? nothing : (this._active ? resetIcon : fa(faChevronDown))}
				</pnx-button>
				<pnx-map-layers-menu
					id="pnx-map-layers-menu"
					._parent=${this._parent}
				></pnx-map-layers-menu>
			</pnx-togglable-group>
		`;
	}
}

customElements.define("pnx-widget-maplayers", MapLayersButton);
